import { Inject, Injectable } from '@angular/core';
import { ApiHttpClient } from 'app/http/clients/api';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { DataListItem } from 'app/borrower-overview/data-list/data-list.component';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { NgxMaskPipe } from 'ngx-mask';
import { NaicsIndustry, NaicsService } from '../naics/naics.service';
import { map } from 'rxjs/operators';
import { Observable, of, catchError } from 'rxjs';
import { StatesService } from 'app/services/states/states.service';
import { AutocompleteOption } from 'app/form-core/formly/autocomplete-type.component';
import { FormlyModel } from 'app/form-core/formly/formly-core.config';
import intervalToDuration from 'date-fns/intervalToDuration';
import formatDuration from 'date-fns/formatDuration';
import _ from 'lodash';
import { LocaleSettingService } from '../i18n/locale-setting.service';
import { PhoneCountryCode } from 'app/models/dto/phone-country-code.model';
import { Zone } from 'app/models/dto/country-data.model';
import { RollbarService } from 'app/rollbar';
import Rollbar from 'rollbar';
import {LenderUser} from 'ng2-app/models/dto/lender-user.model';
import {getFormattedQuarterYear} from 'ng2-app/util/borrowerValues/getQuarterYearStartDateAlias';
import { PhonePipe } from 'app/pipes/phone/phone.pipe';

@Injectable({
  providedIn: 'root'
})
export class BorrowerValueService {

  currencySymbol: string | null;
  phoneCountryCodeMap = {
    borrowerPhone: 'borrowerPhoneCountryCodeId',
    owner_phone: 'ownerPhoneCountryCodeId',
    propertyOwnerPhone: 'propertyOwnerPhoneCountryCodeId',
    'borrower-phone': 'borrowerPhoneCountryCodeId',
    'contact-mobilePhone': 'mobilePhoneCountryCodeId',
  };
  stateAliases = ['borrowerState', 'owner_state'];
  zipAliases = ['borrowerZip', 'owner_zip', 'borrower-zipId'];
  zoneAliases = ['borrowerZone', 'ownerZone'];

  constructor(
    @Inject(RollbarService) private rollbar: Rollbar,
    private http: ApiHttpClient,
    private stateService: StatesService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private maskPipe: NgxMaskPipe,
    private naicsService: NaicsService,
    private localeSettingService: LocaleSettingService,
    private phonePipe: PhonePipe,
  ) {
  }

  forBorrower(borrowerId: number) {
    return this.http.get<BorrowerValue[]>(`/borrower/${borrowerId}/borrower-values`)
      .pipe(
        catchError(() => of())
      );
  }

  save(borrowerId: number, borrowerValues: BorrowerValue[]): Observable<BorrowerValue[]> {
    return this.http.put<BorrowerValue[]>(`/borrower-profile/${borrowerId}`, borrowerValues);
  }

  deleteByAlias(borrowerId: number, alias: string): Observable<BorrowerValue[]> {
    return this.http.delete<BorrowerValue[]>(`/borrower-value/${borrowerId}/alias/${alias}`);
  }

  borrowerValueToDataListItem(
    borrowerValues: BorrowerValue[],
    borrowerValue: BorrowerValue,
    phoneCountryCodes: PhoneCountryCode[],
    zones: Zone[] | null
  ): Observable<DataListItem> {
    let result = this.borrowerValueToString(borrowerValue);

    // normalize everything to observable (thanks Naics!)
    if (!(result instanceof Observable)) {
      result = of(result);
    }

    return result.pipe(
      map(value => {
        const item: DataListItem = {
          label: borrowerValue.shortName || borrowerValue.name,
          value: value,
          alias: borrowerValue.alias
        };

        if (this.zoneAliases.includes(borrowerValue.alias) && zones) {
          const matchingZone = zones.find(z => z.id === parseInt(value));
          item.label = this.localeSettingService.getLocaleZoneCategory();
          item.value = matchingZone ? matchingZone.name : value;
        }

        if (this.stateAliases.includes(borrowerValue.alias)) {
          item.label = this.localeSettingService.getLocaleZoneCategory();
        }

        if (this.zipAliases.includes(borrowerValue.alias)) {
          item.label = this.localeSettingService.getLocalePostalCodeName();
        }

        // hack in verified check
        if (
          borrowerValue.alias === 'creditScore'
          && this.resolveValue({}, borrowerValues, 'personal_credit_score_verified') === '1'
        ) {
          item.metadata = {
            icon: {
              id: 'check_circle',
              color: 'rgb(113, 209, 143)',
              tooltip: 'Verified',
            }
          };
        }

        // Pull in in countryCodeData for phone fields
        if (/phone$/i.test(borrowerValue.alias) && borrowerValue.value) {
          const phoneCountryCodeValue = borrowerValues
            .find(bv => bv.alias === this.phoneCountryCodeMap[borrowerValue.alias]);
          if (phoneCountryCodeValue) {
            const countryCodeItem = this.country(phoneCountryCodeValue.value || '2', phoneCountryCodes);
            if (countryCodeItem) {
              item.metadata = {
                pipe: 'phone',
                countryCode: countryCodeItem.countryCode,
                phoneCountryCode: countryCodeItem.phoneCountryCode,
              };
            }
          }
        }
        return item;
      })
    );
  }

  country(id: string, phoneCountryCodes: PhoneCountryCode[]) {
    if (!phoneCountryCodes || !phoneCountryCodes.length) {
      return;
    }
    return phoneCountryCodes.find(c => c.id === parseInt(id));
  }

  borrowerValuesToFormlyModel(borrowerValues: BorrowerValue[]): FormlyModel {
    borrowerValues = this.getBorrowerValuesWithIntlPhoneValues(borrowerValues);
    return borrowerValues.reduce((collect, borrowerValue) => {
      collect[borrowerValue.alias] = this.toFormlyModelValue(borrowerValue);
      return collect;
    }, {});
  }

  formlyModelToBorrowerValues(borrowerValues: BorrowerValue[], model: FormlyModel): BorrowerValue[] {
    this.setPhonesAndCountryCodeIds(model);

    return borrowerValues.reduce((collect, borrowerValue) => {
      const value = this.fromFormlyModelValue(borrowerValue, model[borrowerValue.alias]);
      if (borrowerValue.value !== value) {
        collect.push(Object.assign({}, borrowerValue, { value }));
      }
      return collect;
    }, [] as BorrowerValue[]);
  }

  borrowerValuesToFormlyFieldConfigs(
    model: FormlyModel,
    borrowerValues: BorrowerValue[],
    lenderUser?: LenderUser,
    tab?: string,
    businessStartDate?: string | null,
    title?: string | null
  ): FormlyFieldConfig[] {
    return borrowerValues
      // We don't want to generate form input for phoneCountryCodeId's
      .filter(borrowerValue => {
        const generateForInputForPhoneCountryCode: boolean = !(/PhoneCountryCodeId$/i.test(borrowerValue.alias));
        const isLocaleUs: boolean = this.localeSettingService.getLocale() === 'en-US';
        const isBvZoneBv: boolean = this.zoneAliases.includes(borrowerValue.alias);
        const keepItemInList: boolean = isBvZoneBv ? isLocaleUs : true;

        return generateForInputForPhoneCountryCode && keepItemInList;
      })
      .map(borrowerValue => this.resolveDependency(borrowerValues, borrowerValue))
      .map(borrowerValue => this.borrowerValueToFormlyFieldConfig(model, borrowerValues, borrowerValue,
                                                                  lenderUser, tab, businessStartDate, title));
  }

  borrowerValueToString(borrowerValue: BorrowerValue): string | Observable<string> {
    if (
      borrowerValue.value === ''
      || borrowerValue.value === null
    ) {
      return '';
    }

    let type = borrowerValue.type;

    // if alias ends in phone, it's phone type, not number, not text
    // all phone numbers should have the same mask
    // phone should be a type, IMO
    // TODO update borrower attributes in db/api/frontends to be phone type
    if (/phone$/i.test(borrowerValue.alias)) {
      type = 'phone';
    }

    switch (type) {
      case 'currency':
        return this.currencyToString(borrowerValue);
      case 'longBool':
      case 'bool':
        return this.boolToString(borrowerValue);
      case 'multiselect':
        return this.multiselectToString(borrowerValue);
      case 'select':
        return this.selectToString(borrowerValue);
      case 'date-input':
        return this.dateinputToString(borrowerValue);
      case 'phone':
        return borrowerValue.value || '';
      case 'number':
        return this.numberToString(borrowerValue);
      case 'naics':
        return this.naicsToString(borrowerValue);
      default:
        return this.textToString(borrowerValue);
    }
  }

  getBvForAttributeWithAllSources(borrowerId: number, attributeId: number): Observable<BorrowerValue[]> {
    return this.http.get<BorrowerValue[]>(`/borrower-value/${borrowerId}/attribute/${attributeId}/source`);
  }

  private borrowerValueToFormlyFieldConfig(
    model: FormlyModel,
    borrowerValues: BorrowerValue[],
    borrowerValue: BorrowerValue,
    lenderUser?: LenderUser,
    tab?: string,
    businessStartDate?: string | null,
    title?: string | null
  ): FormlyFieldConfig {
    let type = borrowerValue.type;

    // if alias ends in phone, it's phone type, not number, not text
    // all phone numbers should have the same mask
    // phone should be a type, IMO
    // TODO update borrower attributes in db/api/frontends to be phone type
    if (/phone$/i.test(borrowerValue.alias)) {
      type = 'phone';
    }

    if (/email$/i.test(borrowerValue.alias)) {
      type = 'email';
    }

    let config;

    switch (type) {
      case 'currency':
        config = this.currencyToFormlyFieldConfig(borrowerValue);
        break;
      case 'longBool':
      case 'bool':
        config = this.boolToFormlyFieldConfig(borrowerValue);
        break;
      case 'multiselect':
        config = this.multiselectToFormlyFieldConfig(borrowerValue);
        break;
      case 'select':
        config = this.selectToFormlyFieldConfig(borrowerValue);
        break;
      case 'date-input':
        config = this.dateinputToFormlyFieldConfig(borrowerValue);
        break;
      case 'number':
        config = this.numberToFormlyFieldConfig(borrowerValue);
        break;
      case 'textarea':
        config = this.textareaToFormlyFieldConfig(borrowerValue);
        break;
      case 'tel':
      case 'phone':
        config = this.phoneToFormlyFieldConfig(borrowerValue);
        break;
      case 'email':
        config = this.emailToFormlyFieldConfig(borrowerValue);
        break;
      case 'naics':
        config = this.naicsToFormlyFieldConfig(borrowerValue);
        break;
      case 'states':
        config = this.stateToFormlyFieldConfig(borrowerValue);
        break;
      case 'zones':
        config = this.zoneToFormlyFieldConfig(borrowerValue);
        break;
      case 'text':
        if (this.zipAliases.includes(borrowerValue.alias)) {
          config = this.zipToFormlyFieldConfig();
        } else {
          config = this.textToFormlyFieldConfig(borrowerValue);
        }
        break;
      case 'ssn':
        config = this.ssnToFormlyFieldConfig(borrowerValue);
        break;
      default:
        throw new Error(`Unrecognized borrowerValue.type in borrower-values-list-smart.component.ts ${borrowerValue.type}`);
    }

    // common configuration
    config.key = borrowerValue.alias;
    if (this.zipAliases.includes(borrowerValue.alias)) {
      config.templateOptions.label = this.localeSettingService.getLocalePostalCodeName();
    } else {
      config.templateOptions.label = borrowerValue.shortName || borrowerValue.name;
    }
    if (type !== 'date-input') { // TODO refactor datepicker to use helpText
      config.templateOptions.helpText = borrowerValue.helpText;
    }
    config.id = this.getFormlyFieldId(borrowerValue.id);
    config.validation = Object.assign(config.validation || {}, {
      show: true
    });

    // dependency to formly hide expression
    config.hideExpression = () => !this.dependencySatisfied(borrowerValue, model);

    // formly field specific attribute
    config.templateOptions.attributes = { 'data-cy': `formly-bv-${borrowerValue.type}-${borrowerValue.alias}` };

    if (borrowerValue.alias === 'pppLoanAmount') {
      config = this.addValidationForPPPLoanAmount(model, borrowerValues, config);
    }

    if (borrowerValue.alias === 'ppp1SbaNumber') {
      config = this.addValidationForFirstPPPLoanNumber(model, borrowerValues, config);
    }

    if (borrowerValue.alias === 'scheduleCIncomeType') {
      config = this.readOnlyToFormlyFieldConfig(borrowerValue);
    }

    if (borrowerValue.alias === 'borrowerCanText') {
      config.templateOptions.disabled = !lenderUser?.permissions.includes('canChangeSmsLegal');
    }

    // erc tab validation
    if (tab === 'erc') {
      this.notRequiredErc(borrowerValue, config);
      this.hideDateFromGrossReceipts(borrowerValue, borrowerValues, config);

      if (businessStartDate && title === 'Gross Receipts') {
        const formattedDate = getFormattedQuarterYear(businessStartDate);
        if (formattedDate && formattedDate > borrowerValue.alias) {
          config.hideExpression = true;
        }
      }
    }

    return config;
  }

  private toFormlyModelValue(borrowerValue: BorrowerValue): any {
    let value: any = borrowerValue.value;

    if (borrowerValue.type === 'multiselect') {
      value = borrowerValue.value?.split(',') || [];
    }

    return value;
  }

  private fromFormlyModelValue(borrowerValue: BorrowerValue, value: any): string {
    if (borrowerValue.type === 'multiselect') {
      return value && value.join ? value.join(',') : value;
    }
    return value;
  }

  private resolveDependency(borrowerValues: BorrowerValue[], borrowerValue: BorrowerValue): BorrowerValue {
    if (borrowerValue.dependency) {
      return borrowerValue;
    }

    const alias = borrowerValue.dependsOn || null;

    borrowerValue.dependency = !alias
      ? null
      : borrowerValues.find(x => x.alias === alias) || null;

    return borrowerValue;
  }

  private getFormlyFieldId(id: any): string {
    return typeof id === 'number' ? id.toFixed() : id;
  }

  private emailToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'text', // TODO build formly 'email' type, with validation built into type
      templateOptions: {
        attributes: { 'data-cy': 'formly-email' },
        pattern: /\S+@\S+\.\S/
      },
      validation: {
        messages: {
          pattern: () => 'Please enter email address in correct format'
        }
      }
    };
  }

  private ssnToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'ssn',
      templateOptions: {
        options: this.stateService.getAllStates(),
        attributes: { 'data-cy': 'formly-ssn' },
        ngxMask: {
          hiddenInput: true,
          patterns: { // TODO move validation into 'ssn' type
            'B': {
              pattern: new RegExp('\\d'),
              symbol: 'X',
            },
            '0': {
              pattern: new RegExp('\\d'),
            },
          }
        }
      }
    };
  }

  private stateToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'states',
      templateOptions: {
        label: 'States',
        attributes: { 'data-cy': 'formly-state' },
      }
    };
  }

  private zoneToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'zones',
      templateOptions: {
        attributes: { 'data-cy': 'formly-zone' },
      },
      expressionProperties: {
        'templateOptions.label': () => this.localeSettingService.getLocaleZoneCategory(),
        'templateOptions.country': () => this.localeSettingService.getLocale()
      }
    };
  }

  private currencyToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'currency',
      templateOptions: {
        attributes: { 'data-cy': 'formly-currency' },
      }
    };
  }

  private boolToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'bool',
      templateOptions: {
        options: [ // TODO move options into 'bool' type
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' }
        ],
        attributes: { 'data-cy': 'formly-bool' }
      }
    };
  }

  private multiselectToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    const options = typeof borrowerValue.options === 'string' ? JSON.parse(borrowerValue.options) :
      borrowerValue.options;
    return {
      type: 'select',
      templateOptions: {
        multiple: true,
        options: options || [],
        attributes: { 'data-cy': 'formly-multiselect' }
      }
    };
  }

  private selectToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    const options = typeof borrowerValue.options === 'string' ? JSON.parse(borrowerValue.options) :
      borrowerValue.options;
    return {
      type: 'select',
      templateOptions: {
        options: options || [],
        attributes: { 'data-cy': 'formly-select' }
      }
    };
  }

  private dateinputToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'datepicker',
      templateOptions: {
        dateHelpText: borrowerValue.helpText,
        attributes: { 'data-cy': 'formly-datepicker' }
      }
    };
  }

  private numberToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    if (borrowerValue.alias === 'ppp1SbaNumber') {
      return {
        type: 'unseparated-number',
        templateOptions: {
          attributes: { 'data-cy': 'formly-sba-number' },
          maxLength: 10,
        },
      };
    }
    return {
      type: 'number',
      templateOptions: {
        attributes: { 'data-cy': 'formly-number' },
      }
    };
  }

  private textareaToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'textarea',
      templateOptions: {
        autosize: true,
        autosizeMinRows: 3,
        attributes: { 'data-cy': 'formly-textarea' }
      }
    };
  }

  private phoneToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'intl-phone',
      templateOptions: {
        attributes: { 'data-cy': 'formly-phone' }
      }
    };
  }

  private naicsToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    const limit = 10; // TODO should limit on backend naics api call
    return {
      type: 'autocomplete',
      templateOptions: {
        attributes: { 'data-cy': 'formly-autocomplete' },
        getOptions: search => this.getNaicsOptions(search, limit),
        getSelected: code => this.getNaicsSelectedOption(code),
      },
    };
  }

  private textToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    return {
      type: 'text',
      templateOptions: {
        attributes: { 'data-cy': 'formly-text' },
        ngxMask: {
          mask: borrowerValue.mask ? this.borrowerValueMaskToNgxMask(borrowerValue.mask || '') : ''
        }
      }
    };
  }

  private zipToFormlyFieldConfig(): FormlyFieldConfig {
    return {
      type: 'text',
      templateOptions: {
        attributes: { 'data-cy': 'formly-zip' },
      },
      validation: {
        show: true,
      },
      validators: {
        zipId: {
          expression: (c) => c.value ? this.localeSettingService.getLocalePostalCodeRegex().test(c.value) : true,
          message: () => 'Invalid format',
        },
      }
    };
  }

  private readOnlyToFormlyFieldConfig(borrowerValue: BorrowerValue): FormlyFieldConfig {
    if (!!borrowerValue.value) {
      return {
        type: 'text',
        templateOptions: {
          label: 'Schedule C Income Type: ' + borrowerValue.value?.toString().charAt(0).toUpperCase()
            + borrowerValue.value?.toString().slice(1),
          disabled: true,
        }
      };
    } else {
      return {
        type: 'text',
        templateOptions: {
          label: 'Schedule C Income Type: N/A',
          disabled: true,
        }
      };
    }
  }

  private currencyToString(borrowerValue: BorrowerValue): string {
    if (!borrowerValue.value) {
      return '';
    }
    const value = parseInt(borrowerValue.value, 10);
    if (isNaN(value)) {
      return '';
    }
    return this.currencyPipe.transform(value, undefined, undefined, '1.0-0') || '';
  }

  private boolToString(borrowerValue: BorrowerValue): string {
    return borrowerValue.value === '1' ? 'Yes' : 'No';
  }

  private multiselectToString(borrowerValue: BorrowerValue): string {
    const values = borrowerValue.value?.split(',') || [];
    const options = borrowerValue.options || [];
    return values.map(value => {
      const selectedOption = options.find(option => option.value === value.trim());
      return selectedOption?.label;
    }).join(', ');
  }

  private selectToString(borrowerValue: BorrowerValue): string {
    const value = borrowerValue.value || '';
    const options = borrowerValue.options || [];
    const selectedOption = options.find(option => option.value === value.trim());
    return selectedOption?.label || '';
  }

  private dateinputToString(borrowerValue: BorrowerValue): string {
    const value = this.datePipe.transform(borrowerValue.value, 'mediumDate');

    if (!value) {
      return '';
    }

    // if alias not in whitelist, return early
    if (
      ![
        'business_start_date'
      ].includes(borrowerValue.alias)
    ) {
      return value;
    }

    const duration = this.dateStringToDurationString(value);
    return `${value} (${duration})`;
  }

  private dateStringToDurationString(date: string): string {
    return formatDuration(intervalToDuration({
      start: Date.now(),
      end: Date.parse(date)
    }), { format: ['years', 'months'] })
      .replace(' years', 'yr')
      .replace(' months', 'mo');
  }

  private numberToString(borrowerValue: BorrowerValue): string {
    const value = borrowerValue.value;
    if (borrowerValue.alias === 'ppp1SbaNumber') {
      return value ? value : '';
    }
    // make sure our value is a string that is not empty or null
    if (value && typeof value === 'string') {
      // if the last character of our string is not the percent symbol
      if (value[value.length - 1] !== '%') {
        try {
          // before we pass it into the decimal pipe, make sure it can be turned into a number at all
          const bvNumber = Number(value);
          return this.decimalPipe.transform(bvNumber, '1.0-0') || '';
        } catch (e: unknown) {
          // if it couldn't be turned into a number, we know it's some sort of character string
          // notify rollbar, then return an empty string
          this.rollbar.error({ error: e });
          return '';
        }
      } else {
        // if the last character is the percent symbol, just return the string as-is
        return value;
      }
      // if it is null or an empty string, just return an empty string
    } else {
      return '';
    }
  }

  private naicsToString(borrowerValue: BorrowerValue): Observable<string> {
    if (!borrowerValue.value) {
      return of('');
    }
    return this.getNaicsSelectedOption(borrowerValue.value).pipe(
      map(option => option.label)
    );
  }

  private textToString(borrowerValue): string {
    let value = borrowerValue.value || '';

    if (borrowerValue.mask) {
      value = this.applyMask(value, borrowerValue.mask);
    }

    return value;
  }

  private applyMask(value: string, mask: string) {
    mask = this.borrowerValueMaskToNgxMask(mask);
    return this.maskPipe.transform(value, mask);
  }

  private borrowerValueMaskToNgxMask(mask: string): string {
    mask = mask.replace(/[#\d]/g, '0'); // ngx-mask uses 0 for numeric placeholders
    return mask;
  }

  private getNaicsOptions(search: string, limit: number): Observable<AutocompleteOption[]> {
    if (search.length < 3) {
      return of([]);
    }

    return this.naicsService.search(search).pipe(
      map(naicsIndustries => naicsIndustries
        .slice(0, limit)
        .map(naicsIndustry => this.naicsIndustryToAutocompleteOption(naicsIndustry)))
    );
  }

  private getNaicsSelectedOption(code: any): Observable<AutocompleteOption> {
    return this.naicsService.find(code).pipe(
      map(naicsIndustry => this.naicsIndustryToAutocompleteOption(naicsIndustry))
    );
  }

  private naicsIndustryToAutocompleteOption(naicsIndustry: NaicsIndustry): AutocompleteOption {
    if (naicsIndustry) {
      return {
        value: naicsIndustry.code,
        label: naicsIndustry.title
      };
    }
    return {
      value: null,
      label: ''
    };
  }

  private dependencySatisfied(borrowerValue: BorrowerValue, model: FormlyModel) {
    if (!borrowerValue.dependency || !borrowerValue.dependsExpression) {
      return true;
    }

    const pattern = new RegExp(borrowerValue.dependsExpression);
    const value = this.fromFormlyModelValue(borrowerValue, model[borrowerValue.dependency.alias]);

    return (
      pattern.test(value) && this.dependencySatisfied(borrowerValue.dependency, model)
    );
  }

  private addValidationForPPPLoanAmount(model: FormlyModel, borrowerValues: BorrowerValue[], config: FormlyFieldConfig): FormlyFieldConfig {
    const valuesToResolve = ['averageMonthlyPayroll', 'eidlGrant', 'industry_naics'];
    return Object.assign({}, config, {
      validators: {
        max: {
          expression: (control) => {
            const [averageMonthlyPayroll, eidlGrant, naicsCode] = this.resolvePPPBorrowerValues(model, borrowerValues, valuesToResolve);
            const max = this.pppLoanAmountMax(Number(averageMonthlyPayroll), Number(eidlGrant), naicsCode);
            return Number(control.value) <= max;
          },
          message: () => {
            const [averageMonthlyPayroll, eidlGrant, naicsCode] = this.resolvePPPBorrowerValues(model, borrowerValues, valuesToResolve);
            const max = this.pppLoanAmountMax(Number(averageMonthlyPayroll), Number(eidlGrant), naicsCode);
            // eslint-disable-next-line max-len
            return `This value should be less than or equal to ${this.localeSettingService.getLocaleCurrencySymbol()}${max.toLocaleString()}`;
          },
        }
      },
    });
  }

  private addValidationForFirstPPPLoanNumber(
    _model: FormlyModel, _borrowerValues: BorrowerValue[], config: FormlyFieldConfig): FormlyFieldConfig {
    const sbaNumberLength = 10;
    return Object.assign({}, config, {
      validators: {
        textLength: {
          expression: (c) => {
            if (c.value) {
              return c.value.length === sbaNumberLength;
            } else {
              return true;
            }
          },
          message: () => 'SBA Loan Numbers must be 10 digits.',
        },
      },
    });
  }

  private resolveValue(model: FormlyModel, borrowerValues: BorrowerValue[], alias: string): any {
    if (alias in model) {
      return model[alias];
    }

    const borrowerValue = borrowerValues.find(x => x.alias === alias);
    if (borrowerValue) {
      return borrowerValue.value;
    }
  }

  private averageMonthlyPayrollMin(pppLoanAmount: number, eidlGrant: number, naicsCode: string): number {
    return (pppLoanAmount - eidlGrant) / this.evaluateMultiplierBasedOnNaics(naicsCode);
  }

  private pppLoanAmountMax(averageMonthlyPayroll: number, eidlGrant: number, naicsCode: string): number {
    return Math.min(averageMonthlyPayroll * this.evaluateMultiplierBasedOnNaics(naicsCode) + eidlGrant, 10000000);
  }

  private resolvePPPBorrowerValues(model: FormlyModel, borrowerValues: BorrowerValue[], valuesToResolve: string[]): string[] {
    const resolvedBorrowerValues: string[] = [];
    valuesToResolve.forEach(name => {
      resolvedBorrowerValues.push(this.resolveValue(model, borrowerValues, name));
    });
    return resolvedBorrowerValues;
  }

  /**
   * A method to find out whether a business is allowed to request 3.5 or 2.5x their amr
   *
   * @param naicsCode
   * @returns The multiplier, either 3.5 or 2.5
   */
  private evaluateMultiplierBasedOnNaics(naicsCode: string | number): number {
    let is72 = false;
    // depending on how this method is accessed, it could be either a string or a number
    if (typeof naicsCode === 'string') {
      // if it's a string, check if it starts with 72
      if (naicsCode.startsWith('72')) {
        is72 = true;
      }
    } else {
      // else it must be a number, so first, we find out the magnitude of the number
      const divisor = Math.floor(Math.log10(naicsCode)) - 1;
      // we then divide the number by its magnitude -1, so we end up with just a magnitude 2 number
      // we then round down, so we just have the two digits of the number
      const naicsFirstTwoDigits = Math.floor(naicsCode / 10 ** divisor);
      if (naicsFirstTwoDigits === 72) {
        is72 = true;
      }
    }
    const amrMultiplier = is72 ? 3.5 : 2.5;
    return amrMultiplier;
  }

  /**
   * Runs through formlyModel and sets phone and phoneCountryCodeId values
   * from intl-phone field object
   *
   * @param model
   * @returns void
   */
  private setPhonesAndCountryCodeIds(model: any) {
    for (const key in model) {
      if (/phone$/i.test(key)) {
        if (model[key]) {
          model[this.phoneCountryCodeMap[key]] = model[key].countryCode;
          model[key] = model[key].phone;
        }
      }
    }
  }

  /**
   *
   * @param borrowerValues
   * @returns [] of BorrowerValues with phone and countryCodeId set as an object
   * for each phone field. (For use with intl-phone component)
   */
  private getBorrowerValuesWithIntlPhoneValues(borrowerValues: any) {
    return borrowerValues.map((borrowerValue => {
      if (/phone$/i.test(borrowerValue.alias) && borrowerValue.value !== null && typeof borrowerValue.value !== 'object') {
        const newBorrowerValue = Object.assign({}, borrowerValue);
        const countryCode = borrowerValues.find(bv => bv.alias === this.phoneCountryCodeMap[borrowerValue.alias]);
        const phone = this.phonePipe.transform(borrowerValue.value).replace(/[()\s-]/g, '');
        newBorrowerValue.value = {
          phone: phone,
          countryCode: countryCode === null || countryCode === undefined || countryCode.value === null
            ? 2
            : parseInt(countryCode.value)
        };
        return newBorrowerValue;
      }
      return borrowerValue;
    }));
  }

  private notRequiredErc(borrowerValue, config) {
    if (borrowerValue.alias === 'borrowerMailingStreet' ||
      borrowerValue.alias === 'borrowerMailingCity' ||
      borrowerValue.alias === 'borrowerMailingState' ||
      borrowerValue.alias === 'borrowerMailingZip' ||
      borrowerValue.alias === 'employed_relatives_names') {
      return;
    }
  }

  private hideDateFromGrossReceipts(borrowerValue, borrowerValues, config) {
    if (Object.values(borrowerValues[0]).includes('quarterlyRevenue2019Q1')) {
      if (borrowerValue.alias === 'operation_start_date') {
        config.hideExpression = true;
      }
    }
  }
}
